// @ts-check
/** @typedef {import('../types/api').ShopTile} ShopTile */

import { ArrowDown, ArrowUp, Eye, EyeOff, Image, Plus, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import shopTilesService from '../services/shopTilesService';
import FileUpload from './FileUpload';
import { FocalPointButton, focalToObjectPosition, toFocal } from './ui/FocalPointPicker';
import ImagePreviewButton from './ui/ImagePreviewButton';

const MAX_TILES = 3;
// Storefront renders every shop tile at 4:5 (desktop grid and mobile carousel).
const TILE_PREVIEWS = [{ label: 'Shop tile 4:5', ratio: '4 / 5' }];

const ShopTilesManager = () => {
  const toast = useToast();
  /** @type {[ShopTile[], Function]} */
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [replacingTileId, setReplacingTileId] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTiles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await shopTilesService.getAll();
      setTiles(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Failed to load shop tiles');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadTiles();
  }, [loadTiles]);

  const handleUploadComplete = async (files) => {
    for (const file of files) {
      if (!file.uploaded || !file.url) continue;

      if (replacingTileId) {
        // Land the new image as a pending replacement — the live tile is untouched
        // until the admin reviews the focal point and clicks Publish (see below).
        try {
          const updated = await shopTilesService.update(replacingTileId, {
            pending_image_url: file.url,
            pending_cloudinary_public_id: file.public_id,
            pending_focal_x: null,
            pending_focal_y: null,
          });
          setTiles((prev) => prev.map((t) => (t.id === replacingTileId ? { ...t, ...updated } : t)));
          toast.success('Replacement uploaded — set a focal point and publish when ready');
        } catch (err) {
          toast.error('Failed to upload replacement');
          console.error(err);
        }
        setReplacingTileId(null);
        setShowUpload(false);
        return;
      }

      // Create new tile, inactive: a fresh upload hasn't been reviewed for its focal
      // point yet, so it shouldn't reach the live homepage until the admin activates it.
      try {
        const newTile = await shopTilesService.create({
          image_url: file.url,
          cloudinary_public_id: file.public_id,
          title: '',
          link_url: '/',
          is_active: false,
        });
        setTiles((prev) => [...prev, newTile]);
        toast.success('Tile added as inactive — set a focal point, then activate it to go live');
      } catch (err) {
        toast.error('Failed to create tile');
        console.error(err);
      }
    }
    setShowUpload(false);
  };

  const handleReorder = async (tileId, direction) => {
    const idx = tiles.findIndex((t) => t.id === tileId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= tiles.length) return;

    const updated = [...tiles];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((t, i) => (t.display_order = i));
    setTiles(updated);

    try {
      await shopTilesService.reorder(updated.map((t) => ({ id: t.id, display_order: t.display_order })));
    } catch (err) {
      toast.error('Failed to reorder tiles');
      console.error(err);
    }
  };

  const handleToggleActive = async (tile) => {
    try {
      await shopTilesService.update(tile.id, { is_active: !tile.is_active });
      setTiles((prev) => prev.map((t) => (t.id === tile.id ? { ...t, is_active: !t.is_active } : t)));
    } catch (err) {
      toast.error('Failed to toggle tile');
      console.error(err);
    }
  };

  const handleDelete = async (tileId) => {
    if (!window.confirm('Delete this tile?')) return;
    try {
      await shopTilesService.delete(tileId);
      setTiles((prev) => prev.filter((t) => t.id !== tileId));
      toast.success('Tile deleted');
    } catch (err) {
      toast.error('Failed to delete tile');
      console.error(err);
    }
  };

  const handleFieldChange = (tileId, field, value) => {
    setTiles((prev) => prev.map((t) => (t.id === tileId ? { ...t, [field]: value } : t)));
  };

  // Persist a focal point; rethrows so the picker stays open on failure.
  const handleSaveFocal = async (tileId, focal) => {
    try {
      const updated = await shopTilesService.update(tileId, { focal_x: focal.x, focal_y: focal.y });
      setTiles((prev) => prev.map((t) => (t.id === tileId ? { ...t, ...updated } : t)));
      toast.success('Focal point saved');
    } catch (err) {
      toast.error('Failed to save focal point');
      console.error(err);
      throw err;
    }
  };

  // Persist the pending focal point (separate from the live one above).
  const handleSavePendingFocal = async (tileId, focal) => {
    try {
      const updated = await shopTilesService.update(tileId, { pending_focal_x: focal.x, pending_focal_y: focal.y });
      setTiles((prev) => prev.map((t) => (t.id === tileId ? { ...t, ...updated } : t)));
      toast.success('Focal point saved');
    } catch (err) {
      toast.error('Failed to save focal point');
      console.error(err);
      throw err;
    }
  };

  // Publishing copies the pending_* fields onto the live ones and clears them;
  // discarding just clears them. Either way it's a single PATCH.
  const handlePublishReplacement = async (tile) => {
    try {
      const updated = await shopTilesService.update(tile.id, {
        image_url: tile.pending_image_url,
        cloudinary_public_id: tile.pending_cloudinary_public_id,
        focal_x: tile.pending_focal_x,
        focal_y: tile.pending_focal_y,
        pending_image_url: null,
        pending_cloudinary_public_id: null,
        pending_focal_x: null,
        pending_focal_y: null,
      });
      setTiles((prev) => prev.map((t) => (t.id === tile.id ? { ...t, ...updated } : t)));
      toast.success('Replacement published');
    } catch (err) {
      toast.error('Failed to publish replacement');
      console.error(err);
    }
  };

  const handleDiscardReplacement = async (tile) => {
    try {
      const updated = await shopTilesService.update(tile.id, {
        pending_image_url: null,
        pending_cloudinary_public_id: null,
        pending_focal_x: null,
        pending_focal_y: null,
      });
      setTiles((prev) => prev.map((t) => (t.id === tile.id ? { ...t, ...updated } : t)));
      toast.success('Replacement discarded');
    } catch (err) {
      toast.error('Failed to discard replacement');
      console.error(err);
    }
  };

  const handleSaveTile = async (tile) => {
    setSaving(true);
    try {
      await shopTilesService.update(tile.id, {
        title: tile.title,
        link_url: tile.link_url,
      });
      toast.success('Tile updated');
    } catch (err) {
      toast.error('Failed to save tile');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading shop tiles...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Add Tile Button */}
      <div className="flex justify-end">
        {showUpload ? (
          <button
            onClick={() => {
              setShowUpload(false);
              setReplacingTileId(null);
            }}
            className="flex items-center gap-2 border border-input text-foreground/80 px-4 py-2 rounded-lg hover:bg-accent transition"
          >
            <X size={18} />
            Cancel
          </button>
        ) : tiles.length < MAX_TILES ? (
          <button
            onClick={() => {
              setShowUpload(true);
              setReplacingTileId(null);
            }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition"
          >
            <Plus size={18} />
            Add Tile
          </button>
        ) : (
          <span className="text-xs text-muted-foreground/70">Maximum {MAX_TILES} tiles reached</span>
        )}
      </div>

      {/* Upload Area */}
      {showUpload && (
        <div className="bg-card border border-border rounded-lg p-4">
          {replacingTileId && (
            <p className="text-sm text-muted-foreground mb-3">
              <RefreshCw size={14} className="inline mr-1" />
              Replacing image for tile — upload a new image
            </p>
          )}
          <FileUpload
            uploadType="shop-tile-image"
            accept="image/*"
            multiple={false}
            maxFiles={1}
            onUploadComplete={handleUploadComplete}
          />
        </div>
      )}

      {/* Tiles List */}
      {tiles.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No shop tiles configured. Add your first tile to get started.
        </div>
      ) : (
        <div className="space-y-3">
          {tiles.map((tile, index) => (
            <div
              key={tile.id}
              className={`flex flex-wrap sm:flex-nowrap items-start gap-4 p-4 border border-border rounded-lg transition ${
                tile.is_active ? 'bg-card' : 'bg-muted opacity-60'
              }`}
            >
              {/* Thumbnail with Replace button */}
              <div className="flex-shrink-0 flex flex-col gap-2">
                <div className="w-[120px] h-[160px] bg-gray-900 rounded-lg overflow-hidden relative group">
                  {tile.image_url ? (
                    <>
                      <ImagePreviewButton src={tile.image_url} alt="Tile" />
                      <FocalPointButton
                        src={tile.image_url}
                        value={toFocal(tile.focal_x, tile.focal_y)}
                        previews={TILE_PREVIEWS}
                        onSave={(f) => handleSaveFocal(tile.id, f)}
                      />
                      <img
                        src={tile.image_url}
                        alt="Tile"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: focalToObjectPosition(toFocal(tile.focal_x, tile.focal_y)) }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={32} className="text-muted-foreground/70" />
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setReplacingTileId(tile.id);
                      setShowUpload(true);
                    }}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="Replace image"
                  >
                    <RefreshCw size={20} className="text-white" />
                  </button>
                </div>

                {/* Pending replacement — uploaded but not yet live; the thumbnail above
                    still shows what customers currently see. */}
                {tile.pending_image_url && (
                  <div className="flex flex-col gap-1 p-2 border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 rounded-lg">
                    <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide">
                      Pending
                    </span>
                    <div className="w-[104px] h-[139px] bg-gray-900 rounded-lg overflow-hidden relative group">
                      <FocalPointButton
                        src={tile.pending_image_url}
                        value={toFocal(tile.pending_focal_x, tile.pending_focal_y)}
                        previews={TILE_PREVIEWS}
                        title="Set focal point — pending image"
                        onSave={(f) => handleSavePendingFocal(tile.id, f)}
                      />
                      <img
                        src={tile.pending_image_url}
                        alt="Pending replacement"
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: focalToObjectPosition(toFocal(tile.pending_focal_x, tile.pending_focal_y)),
                        }}
                      />
                    </div>
                    <button
                      onClick={() => handlePublishReplacement(tile)}
                      className="text-[11px] px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                    >
                      Publish
                    </button>
                    <button
                      onClick={() => handleDiscardReplacement(tile)}
                      className="text-[11px] px-2 py-1 border border-input rounded-md hover:bg-accent transition"
                    >
                      Discard
                    </button>
                  </div>
                )}
              </div>

              {/* Editable Fields */}
              <div className="flex-1 space-y-2">
                <span className="text-xs text-muted-foreground/70">#{index + 1}</span>

                <div className="space-y-2">
                  <input
                    type="text"
                    value={tile.title || ''}
                    onChange={(e) => handleFieldChange(tile.id, 'title', e.target.value)}
                    placeholder="Label (e.g. SHOP OCCASION)"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                  <input
                    type="text"
                    value={tile.link_url || ''}
                    onChange={(e) => handleFieldChange(tile.id, 'link_url', e.target.value)}
                    placeholder="Link URL (e.g. /collection/occasion)"
                    className="w-full px-3 py-1.5 border border-input rounded-lg text-sm focus:outline-none focus:border-foreground"
                  />
                </div>

                <button
                  onClick={() => handleSaveTile(tile)}
                  disabled={saving}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition mt-1"
                >
                  <Save size={14} />
                  Save
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1 flex-shrink-0">
                <button
                  onClick={() => handleReorder(tile.id, 'up')}
                  disabled={index === 0}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move up"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={() => handleReorder(tile.id, 'down')}
                  disabled={index === tiles.length - 1}
                  className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                  title="Move down"
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  onClick={() => handleToggleActive(tile)}
                  className="p-1.5 rounded-lg hover:bg-accent transition"
                  title={tile.is_active ? 'Deactivate' : 'Activate'}
                >
                  {tile.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
                <button
                  onClick={() => handleDelete(tile.id)}
                  className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShopTilesManager;
